import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API } from '../services/api';
import GlobalHeader from '../components/GlobalHeader';
import './SignatureSettingsPage.css';

function SignatureSettingsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [signature, setSignature] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    // Form state
    const [enableDates, setEnableDates] = useState(false);
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [priority, setPriority] = useState(0);

    // Assignments state
    const [assignments, setAssignments] = useState({ groups: [], users: [] });
    const [loadingAssignments, setLoadingAssignments] = useState(true);

    useEffect(() => {
        loadSignature();
        loadAssignments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadSignature = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await API.getSignature(id);
            const sig = response.data.signature;
            setSignature(sig);

            // Initialiser le formulaire avec les valeurs existantes
            if (sig.valid_from || sig.valid_until) {
                setEnableDates(true);
                setValidFrom(sig.valid_from || '');
                setValidUntil(sig.valid_until || '');
            }
            setPriority(sig.priority || 0);

        } catch (err) {
            console.error('Failed to load signature:', err);
            setError('Erreur lors du chargement de la signature');
        } finally {
            setLoading(false);
        }
    };

    const loadAssignments = async () => {
        try {
            setLoadingAssignments(true);
            const response = await API.getSignatureAssignments(id);
            if (response && response.data) {
                setAssignments({
                    groups: response.data.groups || [],
                    users: response.data.users || []
                });
            }
        } catch (err) {
            console.error('Failed to load assignments:', err);
            // Silently fail - non-critical feature
        } finally {
            setLoadingAssignments(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (enableDates) {
            if (!validFrom || !validUntil) {
                setError('Les deux dates sont requises si les dates sont activées');
                return;
            }
            if (new Date(validFrom) >= new Date(validUntil)) {
                setError('La date de début doit être antérieure à la date de fin');
                return;
            }
        }

        try {
            setSaving(true);
            setError('');

            const settings = {
                valid_from: enableDates ? validFrom : null,
                valid_until: enableDates ? validUntil : null,
                priority: parseInt(priority),
                ad_group_ids: null // Pour plus tard
            };

            await API.updateSignatureSettings(id, settings);
            setMessage('Paramètres enregistrés avec succès');

            // Retourner à la liste après 1 seconde
            setTimeout(() => {
                navigate('/signatures');
            }, 1000);

        } catch (err) {
            console.error('Failed to save settings:', err);
            setError('Erreur lors de l\'enregistrement des paramètres');
        } finally {
            setSaving(false);
        }
    };

    // Auto-hide messages
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    if (loading) {
        return (
            <div className="signature-settings-page">
                <GlobalHeader />
                <div className="settings-container">
                    <div className="loading-container">
                        <div className="spinner-large"></div>
                        <p>Chargement...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!signature) {
        return (
            <div className="signature-settings-page">
                <GlobalHeader />
                <div className="settings-container">
                    <div className="error-message">
                        Signature non trouvée
                    </div>
                    <Link to="/signatures" className="btn-secondary">
                        ← Retour aux signatures
                    </Link>
                </div>
            </div>
        );
    }

    const isDefault = signature.is_default;

    return (
        <div className="signature-settings-page">
            <GlobalHeader />

            <div className="settings-container">
                <div className="settings-header">
                    <Link to="/signatures" className="back-link">
                        <svg
                            width="20"
                            height="20"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Retour aux signatures
                    </Link>
                    <h1>Paramètres de "{signature.name}"</h1>
                </div>

                {message && (
                    <div className="message success-message">
                        {message}
                    </div>
                )}

                {error && (
                    <div className="message error-message">
                        {error}
                    </div>
                )}

                {isDefault && (
                    <div className="warning-banner">
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <strong>Cette signature est définie par défaut.</strong>
                            <p>Il n'est pas possible de paramétrer des dates, priorités ou groupes AD pour une signature par défaut.
                               La signature par défaut est un filet de sécurité toujours actif pour garantir qu'un utilisateur a toujours une signature valide.</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="settings-form">
                    {/* Section Plages de dates */}
                    <div className={`settings-section ${isDefault ? 'disabled' : ''}`}>
                        <h2>Plages de dates</h2>
                        <p className="section-description">
                            Définir une période pendant laquelle cette signature sera active (signatures événementielles : Noël, événements spéciaux, etc.)
                        </p>

                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={enableDates}
                                    onChange={(e) => setEnableDates(e.target.checked)}
                                    disabled={isDefault}
                                />
                                <span>Activer la signature pour une période spécifique</span>
                            </label>
                        </div>

                        {enableDates && (
                            <div className="date-range">
                                <div className="form-group">
                                    <label htmlFor="valid_from">Du</label>
                                    <input
                                        type="date"
                                        id="valid_from"
                                        value={validFrom}
                                        onChange={(e) => setValidFrom(e.target.value)}
                                        disabled={isDefault}
                                        required={enableDates}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="valid_until">Au</label>
                                    <input
                                        type="date"
                                        id="valid_until"
                                        value={validUntil}
                                        onChange={(e) => setValidUntil(e.target.value)}
                                        disabled={isDefault}
                                        required={enableDates}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section Priorité */}
                    <div className={`settings-section ${isDefault ? 'disabled' : ''}`}>
                        <h2>Priorité</h2>
                        <p className="section-description">
                            En cas de conflit (plusieurs signatures actives en même temps), la priorité détermine quelle signature utiliser.
                            Une priorité plus élevée sera prioritaire.
                        </p>

                        <div className="form-group">
                            <label htmlFor="priority">
                                Niveau de priorité (0-100)
                            </label>
                            <div className="priority-input-group">
                                <input
                                    type="range"
                                    id="priority"
                                    min="0"
                                    max="100"
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                    disabled={isDefault}
                                />
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={priority}
                                    onChange={(e) => setPriority(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                    disabled={isDefault}
                                    className="priority-number"
                                />
                            </div>
                            <small className="help-text">
                                0 = Priorité normale | 100 = Priorité maximale
                            </small>
                        </div>
                    </div>

                    {/* Section Assignations */}
                    <div className="settings-section">
                        <h2>Assignations</h2>
                        <p className="section-description">
                            Groupes et utilisateurs auxquels cette signature est assignée.
                        </p>

                        {loadingAssignments ? (
                            <div className="assignments-loading">
                                <div className="spinner-small"></div>
                                <span>Chargement des assignations...</span>
                            </div>
                        ) : (
                            <>
                                {/* Groupes assignés */}
                                <div className="assignments-subsection">
                                    <h3>
                                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Groupes ({assignments.groups.length})
                                    </h3>
                                    {assignments.groups.length > 0 ? (
                                        <div className="assignments-list">
                                            {assignments.groups.map(group => (
                                                <div key={group.id} className="assignment-item assignment-group">
                                                    <div className="assignment-info">
                                                        <span className="assignment-name">{group.name}</span>
                                                        {group.description && (
                                                            <span className="assignment-description">{group.description}</span>
                                                        )}
                                                    </div>
                                                    <div className="assignment-meta">
                                                        <span className="assignment-badge">{group.member_count} membres</span>
                                                        <span className="assignment-priority" title="Priorité du groupe">P{group.group_priority}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="no-assignments">Aucun groupe assigné</p>
                                    )}
                                </div>

                                {/* Utilisateurs assignés */}
                                <div className="assignments-subsection">
                                    <h3>
                                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Utilisateurs ({assignments.users.length})
                                    </h3>
                                    {assignments.users.length > 0 ? (
                                        <div className="assignments-list">
                                            {assignments.users.map(user => (
                                                <div key={user.id} className="assignment-item assignment-user">
                                                    <div className="assignment-info">
                                                        <span className="assignment-name">
                                                            {user.first_name} {user.last_name}
                                                            {user.is_shared_mailbox && (
                                                                <span className="shared-badge" title="Boîte partagée">Partagée</span>
                                                            )}
                                                        </span>
                                                        <span className="assignment-email">{user.email}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="no-assignments">Aucun utilisateur assigné directement</p>
                                    )}
                                </div>

                                {/* CTA vers Annuaire */}
                                <div className="assignments-cta">
                                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="cta-content">
                                        <p>Pour assigner cette signature à des groupes ou des utilisateurs, rendez-vous dans le menu <strong>Annuaire</strong>.</p>
                                        <div className="cta-links">
                                            <Link to="/directory#groups" className="cta-link">
                                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                Gérer les groupes
                                            </Link>
                                            <Link to="/directory#users" className="cta-link">
                                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                Gérer les utilisateurs
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Boutons d'action */}
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/signatures')}
                            className="btn-secondary"
                            disabled={saving}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={saving || isDefault}
                        >
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SignatureSettingsPage;
